// src/components/BroadcastModalProvider.tsx
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, Typography, Box, Paper, Button } from "@mui/material";
import Zoom from "@mui/material/Zoom";
import api from "../api/axios";
import gorillaSrc from "../imagenes/pregonerofeliz.png";
import { useTranslation } from "react-i18next";

type Notif = {
  id: number;
  event: string;
  payload: Record<string, any>;
  createdAt: string;
};

type Ctx = {
  enqueueBroadcast: (n: Notif) => void;
};

const BroadcastModalCtx = createContext<Ctx | null>(null);

export function useBroadcastModal() {
  const ctx = useContext(BroadcastModalCtx);
  if (!ctx) throw new Error("useBroadcastModal must be used inside <BroadcastModalProvider>");
  return ctx;
}

export default function BroadcastModalProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation<"translation">();
  const [queue, setQueue] = useState<Notif[]>([]);
  const current = queue[0] || null;

  // Traer BROADCAST iniciales
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notifications", {
          params: { unread: true, take: 50, channel: "IN_APP" },
        });
        const lista = Array.isArray(data) ? data : ((data as any).rows ?? []);
        const onlyBroadcasts = (lista as Notif[]).filter((n) => n.event === "BROADCAST");
        if (onlyBroadcasts.length) {
          setQueue((prev) => {
            const ids = new Set(prev.map((x) => x.id));
            const merged = [...prev, ...onlyBroadcasts.filter((x) => !ids.has(x.id))];
            return merged.sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
          });
        }
      } catch (e) {
        // Error leyendo BROADCAST no leídos
      }
    })();
  }, []);

  // Polling cada 20s
  useEffect(() => {
    let stop = false;
    const fetchUnreadBroadcasts = async () => {
      try {
        const { data } = await api.get("/notifications", {
          params: { unread: true, take: 50, channel: "IN_APP" },
        });
        const lista = Array.isArray(data) ? data : ((data as any).rows ?? []);
        const onlyBroadcasts = (lista as Notif[]).filter((n) => n.event === "BROADCAST");
        if (!onlyBroadcasts.length) return;
        setQueue((prev) => {
          const ids = new Set(prev.map((x) => x.id));
          const merged = [...prev, ...onlyBroadcasts.filter((x) => !ids.has(x.id))];
          return merged.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        });
      } catch {
        /* silencioso */
      }
    };
    fetchUnreadBroadcasts();
    const id = setInterval(() => {
      if (!stop) fetchUnreadBroadcasts();
    }, 20_000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, []);

  // Encolar desde socket
  const enqueueBroadcast = useCallback((n: Notif) => {
    if (n.event !== "BROADCAST") return;
    setQueue((prev) => {
      if (prev.some((x) => x.id === n.id)) return prev;
      return [n, ...prev];
    });
  }, []);

  // Marcar leído y siguiente
  const handleAcknowledge = useCallback(async () => {
    if (!current) return;
    try {
      await api.patch(`/notifications/${current.id}/read`);
    } catch (e) {
      // Error marcando como leída
    } finally {
      setQueue((prev) => prev.slice(1));
    }
  }, [current]);

  const ctxValue = useMemo<Ctx>(() => ({ enqueueBroadcast }), [enqueueBroadcast]);

  return (
    <BroadcastModalCtx.Provider value={ctxValue}>
      {children}

      <Dialog
        open={!!current}
        TransitionComponent={Zoom}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: "50vh", // altura fija real
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogContent
          sx={{
            flex: 1,
            p: 2,
            display: "flex",
            alignItems: "stretch",
            overflow: "hidden",
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              display: "flex",
              alignItems: "stretch",
              p: 2,
              borderRadius: 2,
              width: "100%",
              gap: 2,
              height: "100%",
            }}
          >
            {/* IZQUIERDA */}
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Box
                component="img"
                src={gorillaSrc}
                alt=""
                sx={{
                  maxHeight: "100%", // 🔹 ajusta la imagen al alto del modal
                  maxWidth: "100%",
                  filter: "drop-shadow(0 3px 10px rgba(0,0,0,.25))",
                }}
              />
            </Box>

            {/* DERECHA */}
            <Box
              sx={{
                flex: 1,
                display: "grid",
                gridTemplateRows: "auto 1fr auto",
                height: "100%",
                overflow: "hidden",
                textAlign: "center",
              }}
            >
              {/* TOP */}
              <Box sx={{ pt: 0 }}>
                <Typography variant="subtitle1" fontWeight="bold" fontStyle="italic">
                  {(current?.payload?.title || "Aviso").toUpperCase()}
                </Typography>
              </Box>

              {/* MIDDLE con scroll */}
              <Box
                sx={{
                  overflowY: "auto",
                  px: 1,
                  minHeight: 0, // 🔹 clave para que scrollee
                }}
              >
                <Typography
                  variant="body2"
                  fontStyle="italic"
                  sx={{ whiteSpace: "pre-wrap", width: "100%" }}
                >
                  {String(current?.payload?.body || "")}
                </Typography>
              </Box>

              {/* BOTTOM */}
              <Box sx={{ pb: 1 }}>
                <Box
                  sx={{
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    mt: 1,
                    pt: 1,
                  }}
                >
                  <Typography variant="body2" fontStyle="italic" color="text.secondary">
                    GorillaASAP
                  </Typography>
                </Box>

                <Button
                  onClick={handleAcknowledge}
                  variant="contained"
                  sx={{
                    mt: 2,
                    backgroundColor: "#000",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "#333",
                    },
                    mx: "auto",
                  }}
                  autoFocus
                >
                  {t<string>("notificaciones.btn.marcarUna", "Marcar como leída")}
                </Button>
              </Box>
            </Box>
          </Paper>
        </DialogContent>
      </Dialog>
    </BroadcastModalCtx.Provider>
  );
}
